import { AsyncState, Updater, loadingAsyncState } from "widgets-for-react"
import { validatorResultBuilders } from "../../../../../../Shared/Frontend/validators/validatorExpr"
import { dataLayerServices } from "../../../../seo/datalayer"
import { ZipCode } from "../../../utils/ZipcodeChecker"
import { getPostcodeLoader, submitForm } from "./productFormBlock.api"
import { FormInput, ProductFormState, numericValues, possibleSalutationsType, zipCodeUpdaters } from "./productFormBlock.state"

export const productFormUpdaters = {
  inputUpdater: (field: keyof FormInput, newVal: FormInput[typeof field]): Updater<ProductFormState> => s => {
    if (numericValues.contains(field)) {
      if (newVal != '') {
        const parsedVal = parseInt(newVal !== true ? newVal || '' : '')
        newVal = isNaN(parsedVal) ? s.input[field] : parsedVal.toString()
      }
    }

    let newState: ProductFormState = {
      ...s, input: {
        ...s.input,
        [field]: newVal
      }
    }

    return newState
  },
  recaptchaUpdater: (r: string): Updater<ProductFormState> => s => {
    return {
      ...s,
      recaptcha: r
    }
  },
  validateFields: (field?: keyof FormInput): Updater<ProductFormState> => s => {
    if (field && zipCodeUpdaters.contains(field)) s.zipCodeLoader = getPostcodeLoader(s)

    const input: Partial<FormInput> = field ? {
      ...s.validatorInput,
      [field]: s.input[field]
    } : s.input

    return {
      ...s,
      validatorInput: input,
      validator: s.validator.setValueToValidate(s.input, s.input)
    }
  },
  updateValidator: (u: Updater<ProductFormState["validator"]>): Updater<ProductFormState> => s => {
    const newValidator = u(s.validator)
    let validatorResult = newValidator.evaluateResult()
    return ({ ...s, validator: newValidator, validatorResult: validatorResult })
  },
  zipCodeLoaderUpdater: (u: Updater<AsyncState<ZipCode.CheckResponse>>): Updater<ProductFormState> => s => {
    const updatedLoader = u(s.zipCodeLoader)

    if (updatedLoader.kind == 'loaded') {
      return {
        ...s,
        zipCodeLoader: updatedLoader,
        input: {
          ...s.input,
          street: updatedLoader.value.Street,
          city: updatedLoader.value.City,
          postalCode: updatedLoader.value.postcode,
          houseNumber: updatedLoader.value.houseNumber.toString(),
          invalidHousenumberPostcodeCombination: (updatedLoader.value.City == null || updatedLoader.value.Street == null)
        }
      }
    }
    return { ...s, zipCodeLoader: updatedLoader }
  },
  prepareSubmit: (): Updater<ProductFormState> => s0 => {
    const s1 = productFormUpdaters.validateFields()(s0) as ProductFormState
    const s2 = formSegmentUpdaters.validateFields()(s1) as ProductFormState

    const validatorPassed = s2.validatorResult.values.every(v => v.kind == 'passed')
    const productValidatorPassed = s2.productValidatorResult.values.every(v => v.kind == 'passed')

    if (validatorPassed && productValidatorPassed) {
      dataLayerServices.propositionDonationFormDonatePushDatalayer(s2.input.acceptNewsLetter)

      s2.submitState = loadingAsyncState(() => submitForm(s2, s2.recaptcha))
    }
    return s2
  }
}

export const formInfoUpdaters = (updater: (_: Updater<ProductFormState>) => void) => {
  return {
    recaptchaUpdater: (str: string) => {
      updater(productFormUpdaters.recaptchaUpdater(str))
    },
    emailUpdater: (str: string) => {
      updater(productFormUpdaters.inputUpdater('email', str))
    },
    salutationsUpdater: (sal: possibleSalutationsType) => {
      updater(productFormUpdaters.inputUpdater('salutation', sal))
    },
    firstNameUpdater: (str: string) => {
      updater(productFormUpdaters.inputUpdater('firstName', str))
    },
    insertionUpdater: (str: string) => {
      updater(productFormUpdaters.inputUpdater('insertion', str))
    },
    lastNameUpdater: (str: string) => {
      updater(productFormUpdaters.inputUpdater('lastName', str))
    },
    postalCodeUpdater: (str: string) => {
      updater(productFormUpdaters.inputUpdater('postalCode', str))
    },
    houseNumberUpdater: (str: string) => {
      updater(productFormUpdaters.inputUpdater('houseNumber', str))
    },
    houseNumberAdditionUpdater: (str: string) => {
      updater(productFormUpdaters.inputUpdater('houseNumberAddition', str))
    },
    phoneUpdater: (str: string) => {
      updater(productFormUpdaters.inputUpdater('phone', str))
    },
    acceptNewsLetterUpdater: (bool: boolean) => {
      updater(productFormUpdaters.inputUpdater('acceptNewsLetter', bool))
    },
    onBlur: (field: keyof FormInput) => () => {
      updater(productFormUpdaters.validateFields(field))
    },
    onSubmit: (s: ProductFormState) => {
      updater(productFormUpdaters.prepareSubmit())
    },
  }
}

export const formSegmentUpdaters = {
  updateProductValidator: (u: Updater<ProductFormState["productValidator"]>): Updater<ProductFormState> => s => {
    const newValidator = u(s.productValidator)
    let validatorResult = newValidator.evaluateResult()
    return ({ ...s, productValidator: newValidator, productValidatorResult: validatorResult })
  },
  validateFields: (): Updater<ProductFormState> => s => {
    if (s.donationFormLoader.kind !== "loaded") return s
    const form = s.donationFormLoader.value

    if (form.productForm.kind === "product_sale") {
      return {
        ...s,
        productValidatorResult: validatorResultBuilders.passed("")
      }
    }

    return {
      ...s,
      productValidator: s.productValidator.setValueToValidate(form.productForm, form.productForm)
    }
  },
}
